import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { baseURL } from "../import/ApiEndPoint/TestApi";

const RTC_CONFIG = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

export default function SupportSessionDialog({
  open,
  code,
  database,
  onClose,
}) {
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const channelRef = useRef(null);
  const chunkStoreRef = useRef({});
  const appImageRef = useRef(null);
  const remoteTextInputRef = useRef(null);

  const [status, setStatus] = useState("Connecting...");
  const [appOnline, setAppOnline] = useState(false);
  const [webrtcConnected, setWebrtcConnected] = useState(false);
  const [appFrame, setAppFrame] = useState("");
  const [screenName, setScreenName] = useState("Waiting for app...");
  const [lastCommand, setLastCommand] = useState("");
  const [activeInput, setActiveInput] = useState(null);
  const [remoteInputValue, setRemoteInputValue] = useState("");

  useEffect(() => {
    if (!open || !code) return;

    const socket = io(baseURL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setStatus("Socket connected");

      socket.emit("support:web-join", {
        code,
        database,
      });
    });

    socket.on("support:joined", () => {
      setStatus("Support room joined");

      socket.emit("support:web-ready", {
        code,
      });
    });

    socket.on("support:app-online", () => {
      setAppOnline(true);
      setStatus("App online");

      socket.emit("support:web-ready", {
        code,
      });
    });

    socket.on("support:webrtc-offer", async ({ offer }) => {
      try {
        await handleOffer(offer);
      } catch (error) {
        console.log("Handle WebRTC offer error:", error);
        setStatus("WebRTC offer failed");
      }
    });

    socket.on("support:webrtc-ice-candidate", async ({ candidate }) => {
      try {
        if (peerRef.current && candidate) {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (error) {
        console.log("Website ICE candidate error:", error);
      }
    });

    socket.on("support:user-disconnected", ({ role }) => {
      if (role === "app") {
        setAppOnline(false);
        setStatus("App disconnected");
      }
    });

    socket.on("support:session-ended", () => {
      cleanup(false);
      onClose?.();
    });

    socket.on("disconnect", () => {
      setStatus("Socket disconnected");
      setAppOnline(false);
      setWebrtcConnected(false);
    });

    return () => {
      cleanup(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, code, database]);

  const updateActiveInput = (input) => {
    setActiveInput(input || null);

    if (input && typeof input.value === "string") {
      setRemoteInputValue(input.value);
    }
  };

  useEffect(() => {
    if (!activeInput) return;

    const timer = setTimeout(() => {
      remoteTextInputRef.current?.focus?.();
      remoteTextInputRef.current?.select?.();
    }, 120);

    return () => clearTimeout(timer);
  }, [activeInput]);

  const handleOffer = async (offer) => {
    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }

    const peer = new RTCPeerConnection(RTC_CONFIG);
    peerRef.current = peer;

    peer.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit("support:webrtc-ice-candidate", {
          code,
          candidate: event.candidate,
        });
      }
    };

    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      setStatus(`WebRTC ${state}`);

      if (state === "connected") {
        setWebrtcConnected(true);
      }

      if (
        state === "failed" ||
        state === "closed" ||
        state === "disconnected"
      ) {
        setWebrtcConnected(false);
      }
    };

    peer.ondatachannel = (event) => {
      const channel = event.channel;
      channelRef.current = channel;

      channel.onopen = () => {
        setWebrtcConnected(true);
        setStatus("WebRTC data channel connected");
      };

      channel.onclose = () => {
        setWebrtcConnected(false);
        setStatus("WebRTC data channel closed");
      };

      channel.onmessage = (messageEvent) => {
        handleDataMessage(messageEvent.data);
      };
    };

    await peer.setRemoteDescription(new RTCSessionDescription(offer));

    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);

    socketRef.current.emit("support:webrtc-answer", {
      code,
      answer,
    });
  };

  const handleDataMessage = (raw) => {
    try {
      const packet = JSON.parse(raw);

      if (packet.type === "chunk") {
        const { id, index, total, value } = packet;

        if (!chunkStoreRef.current[id]) {
          chunkStoreRef.current[id] = {
            total,
            chunks: [],
          };
        }

        chunkStoreRef.current[id].chunks[index] = value;

        const stored = chunkStoreRef.current[id];
        const receivedCount = stored.chunks.filter(Boolean).length;

        if (receivedCount === total) {
          const fullMessage = stored.chunks.join("");
          delete chunkStoreRef.current[id];
          handleDataMessage(fullMessage);
        }

        return;
      }

      if (packet.type === "frame") {
        setAppOnline(true);
        setAppFrame(packet.image || "");
        setScreenName(packet.screenName || "App");
        updateActiveInput(packet.activeInput || null);
        return;
      }

      if (packet.type === "state") {
        setAppOnline(true);
        setScreenName(packet.screenName || "App");
        updateActiveInput(packet.activeInput || null);
        return;
      }

      if (packet.type === "active-input") {
        updateActiveInput(packet.activeInput || null);
        return;
      }

      if (packet.type === "command-ack") {
        if (packet.command) {
          setLastCommand(
            packet.target
              ? `${packet.command} → ${packet.target}`
              : packet.command,
          );
        }

        if (packet.activeInput) {
          updateActiveInput(packet.activeInput);
        }
      }
    } catch (error) {
      console.log("Website data message parse error:", error);
    }
  };

  const sendPacket = (packet) => {
    const channel = channelRef.current;

    if (!channel || channel.readyState !== "open") {
      return false;
    }

    channel.send(JSON.stringify(packet));
    return true;
  };

  const sendCommand = (command, payload = {}) => {
    setLastCommand(command);

    const sentByWebrtc = sendPacket({
      type: "command",
      command,
      payload,
    });

    if (!sentByWebrtc && socketRef.current) {
      socketRef.current.emit("support:remote-command", {
        code,
        command,
        payload,
      });
    }
  };

  const handleAppImageTap = (event) => {
    if (!appImageRef.current) return;

    const rect = appImageRef.current.getBoundingClientRect();

    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const xPercent = (clickX / rect.width) * 100;
    const yPercent = (clickY / rect.height) * 100;

    if (xPercent < 0 || xPercent > 100 || yPercent < 0 || yPercent > 100) {
      return;
    }

    sendCommand("REMOTE_TAP", {
      xPercent,
      yPercent,
    });

    setTimeout(() => {
      remoteTextInputRef.current?.focus?.();
    }, 250);
  };

  const sendRemoteTextToApp = (value) => {
    sendCommand("REMOTE_TEXT_INPUT", {
      text: value,
    });
  };

  const handleRemoteInputChange = (event) => {
    const value = event.target.value;
    setRemoteInputValue(value);
    sendRemoteTextToApp(value);
  };

  const cleanup = (notifyServer = true) => {
    try {
      if (notifyServer && socketRef.current && code) {
        socketRef.current.emit("support:disconnect-session", { code });
      }

      if (channelRef.current) {
        channelRef.current.close();
      }

      if (peerRef.current) {
        peerRef.current.close();
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    } catch (error) {
      console.log("Support cleanup error:", error);
    }

    socketRef.current = null;
    peerRef.current = null;
    channelRef.current = null;
    chunkStoreRef.current = {};

    setWebrtcConnected(false);
    setAppOnline(false);
    setAppFrame("");
    setActiveInput(null);
    setRemoteInputValue("");
  };

  const handleDisconnect = () => {
    cleanup(true);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>App Support Session</h2>
            <p style={styles.subtitle}>
              Code: <b>{code}</b>
              {database ? ` • Database: ${database}` : ""}
            </p>
          </div>

          <button style={styles.closeBtn} onClick={handleDisconnect}>
            ✕
          </button>
        </div>

        <div style={styles.statusRow}>
          <span
            style={{
              ...styles.pill,
              backgroundColor: appOnline ? "#DCFCE7" : "#FEE2E2",
              color: appOnline ? "#166534" : "#991B1B",
            }}
          >
            {appOnline ? "App Online" : "App Offline"}
          </span>

          <span
            style={{
              ...styles.pill,
              backgroundColor: webrtcConnected ? "#DBEAFE" : "#FEF3C7",
              color: webrtcConnected ? "#1D4ED8" : "#92400E",
            }}
          >
            {webrtcConnected ? "WebRTC Connected" : "WebRTC Waiting"}
          </span>

          <span style={styles.statusText}>{status}</span>
        </div>

        <div style={styles.body}>
          <div style={styles.phone}>
            <div style={styles.phoneSpeaker} />

            <div style={styles.phoneScreen}>
              {appFrame ? (
                <img
                  ref={appImageRef}
                  src={appFrame}
                  alt="App screen"
                  style={styles.frame}
                  onClick={handleAppImageTap}
                />
              ) : (
                <div style={styles.placeholder}>
                  <h3 style={styles.placeholderTitle}>Waiting for app view</h3>
                  <p style={styles.placeholderText}>
                    Keep the mobile support page open and allow remote support.
                  </p>
                </div>
              )}
            </div>

            <div style={styles.screenName}>{screenName}</div>
          </div>

          <div style={styles.controls}>
            <h3 style={styles.controlsTitle}>Remote App Control</h3>

            <p style={styles.controlsText}>
              Click directly on the app preview. On Reports screen, month/year
              dropdowns, buttons, rows, delete selection and edit inputs are
              mapped.
            </p>

            <div style={styles.typingCard}>
              <div style={styles.typingTopRow}>
                <div>
                  <div style={styles.typingTitle}>Type into app</div>
                  <div style={styles.typingHint}>
                    {activeInput
                      ? `Active input: ${activeInput.label || activeInput.key}`
                      : "Click an app input first, then type here."}
                  </div>
                </div>

                {activeInput ? (
                  <span style={styles.activeInputPill}>Ready</span>
                ) : (
                  <span style={styles.noInputPill}>No input</span>
                )}
              </div>

              <input
                ref={remoteTextInputRef}
                style={{
                  ...styles.remoteTextInput,
                  opacity: activeInput ? 1 : 0.65,
                }}
                value={remoteInputValue}
                onChange={handleRemoteInputChange}
                disabled={!activeInput}
                placeholder={
                  activeInput
                    ? "Start typing from your computer keyboard"
                    : "Click an app textbox first"
                }
              />

              <button
                style={styles.remoteTextButton}
                onClick={() => sendRemoteTextToApp(remoteInputValue)}
              >
                Send Text to App
              </button>
            </div>

            <div style={styles.grid}>
              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("OPEN_HOME")}
              >
                Open Home
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("OPEN_ATTENDANCE")}
              >
                Attendance
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("OPEN_FACE_LIST")}
              >
                Face List
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("OPEN_REPORTS")}
              >
                Reports
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("OPEN_MORE")}
              >
                More
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("GO_BACK")}
              >
                Go Back
              </button>

              <button
                style={styles.commandBtn}
                onClick={() =>
                  sendCommand("SHOW_MESSAGE", {
                    message: "Website support user is connected.",
                  })
                }
              >
                Show Message
              </button>

              <button
                style={styles.commandBtn}
                onClick={() => sendCommand("REFRESH_VIEW")}
              >
                Refresh View
              </button>
            </div>

            {lastCommand ? (
              <div style={styles.lastCommand}>
                Last command: <b>{lastCommand}</b>
              </div>
            ) : null}

            <button style={styles.disconnectBtn} onClick={handleDisconnect}>
              Disconnect Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    backgroundColor: "rgba(15,23,42,.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },
  dialog: {
    width: 1050,
    maxWidth: "100%",
    maxHeight: "92vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 18,
    padding: 22,
    boxShadow: "0 28px 80px rgba(0,0,0,.32)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    color: "#111827",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748B",
    fontSize: 14,
  },
  closeBtn: {
    height: 36,
    width: 36,
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  pill: {
    padding: "7px 12px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 900,
  },
  statusText: {
    color: "#64748B",
    fontSize: 13,
    fontWeight: 700,
  },
  body: {
    display: "grid",
    gridTemplateColumns: "370px 1fr",
    gap: 26,
    marginTop: 22,
  },
  phone: {
    width: 340,
    margin: "0 auto",
    background: "#111827",
    borderRadius: 34,
    padding: "14px 12px 16px",
    boxShadow: "0 22px 48px rgba(15,23,42,.28)",
  },
  phoneSpeaker: {
    width: 90,
    height: 6,
    borderRadius: 999,
    background: "#374151",
    margin: "0 auto 12px",
  },
  phoneScreen: {
    height: 610,
    background: "#F8FAFC",
    borderRadius: 24,
    overflow: "hidden",
    border: "1px solid #374151",
  },
  frame: {
    width: "100%",
    height: "100%",
    objectFit: "fill",
    background: "#F8FAFC",
    cursor: "crosshair",
  },
  placeholder: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 24,
    boxSizing: "border-box",
    background: "linear-gradient(180deg,#EFF6FF,#F8FAFC)",
  },
  placeholderTitle: {
    color: "#111827",
    fontSize: 20,
    fontWeight: 900,
    margin: 0,
  },
  placeholderText: {
    color: "#64748B",
    fontSize: 14,
    lineHeight: "21px",
  },
  screenName: {
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
    marginTop: 10,
  },
  controls: {
    minWidth: 0,
  },
  controlsTitle: {
    margin: "0 0 6px",
    fontSize: 21,
    fontWeight: 900,
    color: "#111827",
  },
  controlsText: {
    margin: "0 0 16px",
    color: "#64748B",
    fontSize: 14,
    lineHeight: "21px",
  },
  typingCard: {
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    padding: 14,
    marginBottom: 16,
  },
  typingTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  typingTitle: {
    color: "#111827",
    fontSize: 15,
    fontWeight: 900,
  },
  typingHint: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 2,
  },
  activeInputPill: {
    background: "#DCFCE7",
    color: "#166534",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 12,
    fontWeight: 900,
  },
  noInputPill: {
    background: "#FEE2E2",
    color: "#991B1B",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 12,
    fontWeight: 900,
  },
  remoteTextInput: {
    width: "100%",
    boxSizing: "border-box",
    height: 42,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    outline: "none",
    padding: "0 12px",
    fontSize: 14,
    fontWeight: 800,
    color: "#111827",
    background: "#FFFFFF",
  },
  remoteTextButton: {
    marginTop: 9,
    height: 38,
    width: "100%",
    borderRadius: 11,
    border: 0,
    background: "#2563EB",
    color: "#FFFFFF",
    fontWeight: 900,
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  commandBtn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1D4ED8",
    fontWeight: 900,
    cursor: "pointer",
  },
  lastCommand: {
    marginTop: 16,
    padding: 12,
    borderRadius: 12,
    background: "#F8FAFC",
    border: "1px solid #E5E7EB",
    color: "#334155",
    fontSize: 14,
  },
  disconnectBtn: {
    marginTop: 18,
    height: 44,
    width: "100%",
    borderRadius: 12,
    border: 0,
    background: "#DC2626",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
};
